import React from 'react';
import useCountUp from '@smart-tv/utils/animations/numberCountUp';
import clsx from 'clsx';
import type { NpvrStatusInfo } from '@smart-tv/typings/npvr';

import Typography from '@smart-tv/components/typography/typography';
import styles from './storage-indicator-wheel.module.scss';

//must match the values in storage-indicator-wheel.module.scss to align animations
const ANIMATION_DURATION_MS = 1000;
const VIEWBOX_SIZE = 550;
const STROKE_WIDTH = 50;
const CENTER = VIEWBOX_SIZE / 2;
const RADIUS = (VIEWBOX_SIZE - STROKE_WIDTH) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
// Must match the gap in the `storage-indicator-wheel-fill` keyframe.
const DASH_GAP = 2000;

// This is done to avoid unnecessary React re-renders while the number is counting up.

interface SegmentProps {
  className: string;
  ratio: number;
  isRecorded: boolean;
}

const Segment: React.FC<SegmentProps> = ({ className, ratio, isRecorded }) => {
  const offset = ratio === 1 ? 0 : STROKE_WIDTH / 2;
  return (
    <circle
      className={clsx(styles['storage-indicator-wheel__segment'], className, {
        [styles['storage-indicator-wheel__segment--full']]: isRecorded && ratio === 1
      })}
      cx={CENTER}
      cy={CENTER}
      r={RADIUS}
      style={{
        // Inset by half a stroke at each end so the round caps stay within the segment's arc.
        strokeDasharray: `${Math.max(0, ratio * CIRCUMFERENCE - offset)} ${DASH_GAP}`,
        strokeWidth: STROKE_WIDTH + 'px'
      }}
    />
  );
};

interface Props {
  status: NpvrStatusInfo | null;
}

export const StorageIndicatorWheel: React.FC<Props> = ({ status }) => {
  const recordedRatio = status ? Math.max(0, Math.min(status.recordedSpaceRatio, 1)) : 0;
  const scheduledRatio = status
    ? Math.max(0, Math.min(status.scheduledSpaceRatio + recordedRatio, 1))
    : 0;
  const totalSpaceInMinutes =
    (status?.recordingTime.scheduled.inMinutes ?? 0) + (status?.recordingTime.used.inMinutes ?? 0);
  const displayNumber =
    totalSpaceInMinutes >= 60 ? Math.round(totalSpaceInMinutes / 60) : totalSpaceInMinutes;
  const displayText = totalSpaceInMinutes >= 60 ? (displayNumber === 1 ? 'time' : 'timer') : 'min';
  const aboveLimit = totalSpaceInMinutes > (status?.recordingTime.total.inMinutes ?? 0);
  const numberRef = useCountUp(displayNumber, ANIMATION_DURATION_MS);

  return (
    <div className={styles['storage-indicator-wheel']}>
      <svg
        className={styles['storage-indicator-wheel__svg']}
        viewBox={`0 0 ${VIEWBOX_SIZE} ${VIEWBOX_SIZE}`}
      >
        <circle
          className={styles['storage-indicator-wheel__track']}
          style={{
            strokeWidth: STROKE_WIDTH + 'px'
          }}
          cx={CENTER}
          cy={CENTER}
          r={RADIUS}
        />
        {status ? (
          <>
            {scheduledRatio > 0 ? (
              <Segment
                className={styles['storage-indicator-wheel__segment--scheduled']}
                ratio={scheduledRatio}
                isRecorded={false}
              />
            ) : null}
            {recordedRatio > 0 ? (
              <Segment
                className={styles['storage-indicator-wheel__segment--recorded']}
                ratio={recordedRatio}
                isRecorded={true}
              />
            ) : null}
          </>
        ) : null}
      </svg>
      {status ? (
        <div className={styles['storage-indicator-wheel__label']}>
          <Typography variant="h3" fontWeight="regular" color="labelTint" textAlign="center">
            Brugt
          </Typography>
          <Typography
            variant="h2"
            fontWeight="bold"
            color={aboveLimit ? 'error' : undefined}
            textAlign="center"
          >
            <span ref={numberRef}>0</span> {displayText}
          </Typography>
          <Typography variant="h3" fontWeight="regular" color="labelTint" textAlign="center">
            af dine {status.recordingTime.total.inHours}
          </Typography>
        </div>
      ) : null}
    </div>
  );
};

export default StorageIndicatorWheel;
